/**
 * 销售订单列表页
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Input, Select, Button, Card, Row, Col, Modal, Form, InputNumber, Switch, message } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { salesApi } from '../../services/api';

const statusMap = { unpaid: '未收款', paid: '已收款', shipped: '已发货', installed: '已安装', completed: '已完成', cancelled: '已取消' };
const statusColors = { unpaid: 'orange', paid: 'blue', shipped: 'cyan', installed: 'processing', completed: 'success', cancelled: 'error' };

function SalesList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ status: '', keyword: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();
  const navigate = useNavigate();

  const columns = [
    { title: '销售单号', dataIndex: 'sale_no', width: 170 },
    { title: '客户', dataIndex: 'customer_name', width: 100 },
    { title: '电话', dataIndex: 'phone', width: 120 },
    { title: '产品', dataIndex: 'product_name', width: 150, render: (v) => v || '-' },
    { title: 'SN/ID号', dataIndex: 'sn_number', width: 130, render: (v) => v || '-' },
    { title: '金额', dataIndex: 'amount', width: 100, render: (v) => `¥${parseFloat(v).toFixed(2)}` },
    { title: '状态', dataIndex: 'status', width: 100, render: (s) => <Tag color={statusColors[s]}>{statusMap[s] || s}</Tag> },
    { title: '需安装', dataIndex: 'need_install', width: 80, render: (v) => v === 1 ? '是' : '否' },
    { title: '保修到期', dataIndex: 'warranty_end', width: 110, render: (v) => v || '-' },
    { title: '创建时间', dataIndex: 'created_at', width: 160, render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
    { title: '操作', width: 80, fixed: 'right', render: (_, r) => <Button type="link" onClick={() => navigate(`/sales/detail/${r.id}`)}>详情</Button> },
  ];

  const loadData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await salesApi.list({ page, pageSize, ...filters });
      setData(res.data?.list || []);
      setPagination({ page: res.data?.pagination?.page || 1, pageSize: res.data?.pagination?.pageSize || 20, total: res.data?.pagination?.total || 0 });
    } catch (err) { } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      await salesApi.create(values);
      message.success('销售订单创建成功');
      setCreateOpen(false);
      createForm.resetFields();
      loadData();
    } catch (err) {
      // 已在拦截器处理
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col><Input placeholder="搜索单号/客户/电话" prefix={<SearchOutlined />} value={filters.keyword}
            onChange={e => setFilters({ ...filters, keyword: e.target.value })} onPressEnter={() => loadData()} style={{ width: 250 }} allowClear /></Col>
          <Col>
            <Select placeholder="状态" value={filters.status || undefined} allowClear style={{ width: 140 }}
              onChange={v => setFilters({ ...filters, status: v || '' })}>
              {Object.entries(statusMap).map(([k, v]) => <Select.Option key={k} value={k}>{v}</Select.Option>)}
            </Select>
          </Col>
          <Col><Button type="primary" icon={<SearchOutlined />} onClick={() => loadData()}>搜索</Button></Col>
          <Col><Button icon={<ReloadOutlined />} onClick={() => { setFilters({ status: '', keyword: '' }); setTimeout(() => loadData(), 0); }}>重置</Button></Col>
          <Col flex="auto" />
          <Col><Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>新建订单</Button></Col>
        </Row>
      </Card>
      <Table rowKey="id" columns={columns} dataSource={data} loading={loading}
        pagination={{ ...pagination, showSizeChanger: true, showTotal: (t) => `共 ${t} 条`, onChange: (page, pageSize) => loadData(page, pageSize) }}
        scroll={{ x: 1400 }} />

      <Modal
        title="新建销售订单"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        confirmLoading={createLoading}
        okText="创建"
        cancelText="取消"
        width={640}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_name" label="客户姓名" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="客户地址" rules={[{ required: true, message: '请输入客户地址' }]}>
            <Input placeholder="请输入" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contact_person" label="联系人" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sn_number" label="SN/ID号">
                <Input placeholder="可选" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="product_name" label="产品名称">
                <Input placeholder="如：XX型号主板" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="product_model" label="产品型号">
                <Input placeholder="可选" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="amount" label="金额（元）" rules={[{ required: true, message: '请输入金额' }]}>
                <InputNumber min={0.01} step={0.01} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quantity" label="数量" initialValue={1}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="warranty_months" label="保修（月）" initialValue={12}>
                <InputNumber min={0} max={120} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="need_install" label="需要安装" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SalesList;
